/**
 * browserStage.js - the trial loop's environment in a browser.
 *
 * PLATFORM SCAFFOLD. Everything here is "what a real device does": a
 * synthesiser that takes time to speak, a recogniser that calls back whenever
 * it feels like it, timers, and a microphone. `trialLoop.js` holds the sequence
 * of a trial; this holds nothing about trials at all.
 *
 * The test harness supplies the same interface over a virtual clock and a
 * scripted child (`client/test/support/scriptedStage.js`), which is what makes
 * the loop testable without a browser, a microphone or a child.
 *
 * ---------------------------------------------------------------------------
 * PUSH BECOMES PULL
 *
 * A recogniser pushes: `onresult` fires whenever it fires, including while the
 * game is still talking. The loop pulls: it asks for the next thing that
 * happens, or for the moment the next ladder step is due, whichever comes
 * first. So events that arrive while the loop is busy - awaiting a synthesiser,
 * usually - are QUEUED, never dropped, and delivered on the next ask. A dropped
 * final result is a trial the child answered and the chart never heard about.
 *
 * ---------------------------------------------------------------------------
 * STALE WORK
 *
 * A level can end, a card can be tapped, the component can unmount, all while a
 * trial is mid-await. `abort()` invalidates everything: the pending wait
 * resolves as `abort`, the loop returns `{ cancelled: true }`, and no record is
 * built from a trial nobody is playing any more.
 */

import { UNMONITORED_AUDIO } from '../../../hooks/useAudioEnergy.js';

/** Longest a synthesiser is trusted to report that it has finished. */
const SPEECH_TIMEOUT_MS = 9000;

/**
 * @param {object} input
 * @param {{current:object}} input.env  a ref holding the live hooks:
 *        `{ speech, synth, fx, energy, soundOn }`. A ref, not values, because a
 *        timer scheduled eight seconds ago must not fire into a closure that
 *        captured a stale hook.
 * @param {(patch:object) => void} [input.show]     paint something
 * @param {(text:string|null) => void} [input.onSay] the speech bubble
 * @param {(payload:object) => any} [input.post]    hand an attempt to the queue
 * @returns {object} a stage, plus attach/abort/dispose
 */
export function createBrowserStage({ env, show, onSay, post }) {
  let open = false;
  let queue = [];
  let waiter = null;          // { resolve, timer }
  let stopped = false;
  // Every trial takes a token. A loop parked in an await - a synthesiser that
  // takes two seconds - must not wake up and start driving the trial that
  // replaced it. GAME-01.md section 4 records this as the rule the loop follows
  // that no document had.
  let token = 0;
  let sawOnset = false;
  let monitoring = false;
  const timers = new Set();

  const now = () => performance.now();

  const settle = (event) => {
    if (!waiter) return false;
    const { resolve, timer } = waiter;
    waiter = null;
    if (timer) clearTimeout(timer);
    resolve(event);
    return true;
  };

  /** An event from the device. Dropped unless a trial is listening for it. */
  const push = (event) => {
    if (!open || stopped) return;
    if (!settle(event)) queue.push(event);
  };

  return {
    // ------------------------------------------------------------------ wiring
    /**
     * Register the recogniser's callbacks. Call once, from an effect, and call
     * the returned function on unmount.
     */
    attach() {
      const { speech } = env.current;
      speech.onResult((transcript, meta) => push({
        kind: 'utterance',
        at: now(),
        transcript,
        confidence: meta && meta.confidence !== undefined ? meta.confidence : null,
        alternatives: (meta && meta.alternatives) || [],
      }));
      // The recogniser's own onspeechstart. engine/trial.js is explicit that
      // this is the field it wants and that an interim result is not a
      // substitute for it.
      speech.onSpeechStart?.((at) => push({ kind: 'onset', at }));
      return () => this.dispose();
    },

    /** Everything in flight is now stale. */
    abort() {
      stopped = true;
      open = false;
      queue = [];
      token += 1;
      settle({ kind: 'abort', at: now() });
    },

    dispose() {
      this.abort();
      for (const id of timers) clearTimeout(id);
      timers.clear();
    },

    /** A timer that is cleaned up with the stage. For the game's own pauses. */
    later(ms, fn) {
      const id = setTimeout(() => { timers.delete(id); fn(); }, Math.max(0, ms));
      timers.add(id);
      return id;
    },

    clearTimers() {
      for (const id of timers) clearTimeout(id);
      timers.clear();
    },

    // ------------------------------------------------------------- the stage
    now,

    /**
     * @param {number} [forToken] the token openTrial() returned. Without one,
     *        "has the stage been stopped"; with one, "is that trial still the
     *        trial", which is the question a resumed await has to ask.
     */
    aborted: (forToken) => (forToken === undefined ? stopped : forToken !== token),

    openTrial() {
      queue = [];
      stopped = false;
      sawOnset = false;
      open = true;
      token += 1;
      const { energy } = env.current;
      monitoring = Boolean(energy && energy.isMonitoring);
      // Opened here so that everything endTrial() returns belongs to this trial
      // and nothing before it.
      if (monitoring) energy.startTrial(now());
      return token;
    },

    closeTrial() {
      open = false;
    },

    /**
     * Say something, and resolve when the speaker has actually stopped.
     *
     * The await matters twice over. It gives promptEndedAt a real value instead
     * of a guess, and it keeps two utterances from overlapping -
     * useSpeechSynthesis cancels whatever is speaking when a new line starts
     * (audit finding B46), which truncates the character mid-word.
     */
    async say(text, { praise = false } = {}) {
      if (!text) return;
      if (onSay) onSay(text);
      const { synth, soundOn } = env.current;
      if (!soundOn || !synth.isSupported) return;
      // Some browsers never fire `onend`. Racing a generous estimate means a
      // silent failure costs a pause, not the whole session. Deliberately a
      // bare setTimeout, not `later()`: closing a trial clears the trial's
      // timers, and if this one went with them a browser that never fires
      // `onend` would leave the loop awaiting forever.
      const estimate = Math.min(SPEECH_TIMEOUT_MS, 500 + text.length * 85);
      await Promise.race([
        praise ? synth.speakPraise(text) : synth.speakAsPip(text),
        new Promise((resolve) => { setTimeout(resolve, estimate); }),
      ]);
    },

    show(patch) {
      if (show) show(patch);
    },

    /**
     * The first thing that happens, or `due` when the clock reaches `until`.
     */
    nextEvent({ until, token: forToken }) {
      if (stopped || (forToken !== undefined && forToken !== token)) {
        return Promise.resolve({ kind: 'abort', at: now() });
      }
      if (queue.length) return Promise.resolve(queue.shift());

      return new Promise((resolve) => {
        const delay = Math.max(0, until - now());
        const timer = setTimeout(() => {
          waiter = null;
          // Never report a moment earlier than the one that was waited for:
          // the loop compares this against the next ladder step's due time.
          resolve({ kind: 'due', at: Math.max(until, now()) });
        }, delay);
        waiter = { resolve, timer };
      });
    },

    onsetAccepted() {
      sawOnset = true;
    },

    /**
     * Only when the device offered neither an onset nor audio monitoring. The
     * arrival of the final result is LATE, which costs the child boxes rather
     * than awarding them - the safe direction.
     */
    allowInferredOnset: () => !sawOnset && !monitoring,

    audio({ endedAt }) {
      const { energy } = env.current;
      if (!monitoring || !energy) return UNMONITORED_AUDIO;
      return energy.endTrial(endedAt).audio;
    },

    /** Queued, retried, and never awaited by the child. */
    post(payload) {
      return post ? post(payload) : null;
    },
  };
}

export default { createBrowserStage };
